/**
 * Identify / View Info rows for a picked vector feature.
 *
 * Works for points, lines, and polygons. Derived spatial facts (area,
 * length, vertices) are computed from geometry when present; attribute
 * fields come from the feature property bag.
 */
import type { VectorExportFeature } from '@geosoft/viewer-sdk';
import { centroidFromGeometry } from './vectorAttributeRows';

const ORIGIN_SHIFT = 20037508.342789244;
const SKIP_ATTRIBUTE_KEYS = new Set(['geometry', 'Geometry', 'the_geom', 'shape']);
const ID_KEYS = [
  'OBJECTID_1',
  'OBJECTID',
  'objectid',
  'FID',
  'fid',
  'gid',
  'id',
  'ID',
] as const;

export type IdentifyGeometryKind = 'point' | 'line' | 'polygon' | 'unknown';

export interface IdentifyRow {
  readonly feature: string;
  readonly value: string;
}

export interface IdentifySection {
  readonly id: 'derived' | 'attributes';
  readonly title: string;
  readonly rows: readonly IdentifyRow[];
}

export interface IdentifySnapshot {
  readonly title: string;
  readonly featureId: string | number | null;
  readonly kind: IdentifyGeometryKind;
  readonly sections: readonly IdentifySection[];
}

export function geometryKindOf(geometry: unknown): IdentifyGeometryKind {
  return geometryKindFromTypeName(geometryTypeOf(geometry));
}

/**
 * Layer-level kind for the Vector ToolCard: prefer `style.geometryType`
 * (already stamped on scene items), then the first feature that has geometry.
 */
export function resolveVectorGeometryKind(args: {
  readonly styleGeometryType?: unknown;
  readonly features?: ReadonlyArray<{ readonly geometry?: unknown }>;
}): IdentifyGeometryKind {
  const fromStyle = geometryKindFromTypeName(args.styleGeometryType);
  if (fromStyle !== 'unknown') return fromStyle;
  for (const feature of args.features ?? []) {
    const kind = geometryKindOf(feature.geometry);
    if (kind !== 'unknown') return kind;
  }
  return 'unknown';
}

function geometryKindFromTypeName(value: unknown): IdentifyGeometryKind {
  if (typeof value !== 'string' || value.trim().length === 0) return 'unknown';
  const compact = value.trim().toLowerCase().replace(/[\s_-]/g, '');
  if (compact === 'point' || compact === 'multipoint') return 'point';
  if (
    compact === 'linestring' ||
    compact === 'multilinestring' ||
    compact === 'line' ||
    compact === 'polyline'
  ) {
    return 'line';
  }
  if (compact === 'polygon' || compact === 'multipolygon') return 'polygon';
  if (compact.includes('point')) return 'point';
  if (compact.includes('line')) return 'line';
  if (compact.includes('polygon')) return 'polygon';
  return 'unknown';
}

export function matchIdentifyFeature(
  features: ReadonlyArray<VectorExportFeature>,
  featureId: string | number | null | undefined,
  properties?: Readonly<Record<string, unknown>> | null,
): VectorExportFeature | null {
  if (featureId != null && String(featureId).length > 0) {
    const wanted = String(featureId);
    const byId = features.find((feature) => featureMatchesId(feature, wanted));
    if (byId) return byId;
  }
  if (properties) {
    for (const key of ID_KEYS) {
      const value = properties[key];
      if (value == null || value === '') continue;
      const wanted = String(value);
      const hit = features.find((feature) => featureMatchesId(feature, wanted));
      if (hit) return hit;
    }
  }
  return null;
}

export function buildIdentifySnapshot(args: {
  readonly layerLabel: string;
  readonly featureId?: string | number | null;
  readonly properties?: Readonly<Record<string, unknown>> | null;
  readonly geometry?: unknown;
  readonly clickPoint?: readonly [number, number] | null;
}): IdentifySnapshot {
  const properties = args.properties ?? {};
  const kind = geometryKindOf(args.geometry);
  const featureId =
    args.featureId
    ?? firstId(properties)
    ?? null;
  const derived: IdentifyRow[] = [];

  if (args.clickPoint && args.clickPoint.length >= 2) {
    derived.push(
      { feature: '(clicked x)', value: formatCoord(args.clickPoint[0]) },
      { feature: '(clicked y)', value: formatCoord(args.clickPoint[1]) },
    );
  }

  const centroid = centroidFromGeometry(args.geometry);
  if (centroid && kind === 'point') {
    derived.push(
      { feature: 'Feature X', value: formatCoord(centroid.x) },
      { feature: 'Feature Y', value: formatCoord(centroid.y) },
    );
  }

  const spatial = measureGeometry(args.geometry, kind);
  if (spatial.areaM2 != null) {
    derived.push({ feature: 'Area', value: `${formatMeasure(spatial.areaM2)} m²` });
  }
  if (spatial.lengthM != null) {
    derived.push({
      feature: kind === 'polygon' ? 'Perimeter' : 'Length',
      value: `${formatMeasure(spatial.lengthM)} m`,
    });
  }
  if (spatial.vertices != null) {
    derived.push({ feature: 'Vertices', value: String(spatial.vertices) });
  }
  if (featureId != null) {
    derived.push({ feature: 'Feature ID', value: String(featureId) });
  }

  const attributes: IdentifyRow[] = [];
  for (const [key, value] of Object.entries(properties)) {
    if (SKIP_ATTRIBUTE_KEYS.has(key)) continue;
    attributes.push({ feature: key, value: formatAttribute(value) });
  }

  const title =
    stringProp(properties, 'SUB_DIST')
    ?? stringProp(properties, 'DISTRICT')
    ?? stringProp(properties, 'name')
    ?? stringProp(properties, 'NAME')
    ?? stringProp(properties, 'title')
    ?? args.layerLabel;

  return {
    title,
    featureId,
    kind,
    sections: [
      { id: 'derived', title: 'Derived', rows: derived },
      { id: 'attributes', title: 'Attributes', rows: attributes },
    ],
  };
}

function featureMatchesId(feature: VectorExportFeature, wanted: string): boolean {
  if (feature.id != null && String(feature.id) === wanted) return true;
  const props = feature.properties ?? {};
  return ID_KEYS.some((key) => props[key] != null && String(props[key]) === wanted);
}

function firstId(properties: Readonly<Record<string, unknown>>): string | number | null {
  for (const key of ID_KEYS) {
    const value = properties[key];
    if (typeof value === 'string' || typeof value === 'number') return value;
  }
  return null;
}

function stringProp(properties: Readonly<Record<string, unknown>>, key: string): string | null {
  const value = properties[key];
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function geometryTypeOf(geometry: unknown): string | null {
  if (!geometry || typeof geometry !== 'object') return null;
  const record = geometry as { type?: unknown; getType?: () => string };
  if (typeof record.getType === 'function') return record.getType();
  return typeof record.type === 'string' ? record.type : null;
}

function measureGeometry(
  geometry: unknown,
  kind: IdentifyGeometryKind,
): { areaM2: number | null; lengthM: number | null; vertices: number | null } {
  const rings = ringsOf(geometry);
  if (rings.length === 0) {
    return { areaM2: null, lengthM: null, vertices: null };
  }
  let areaM2 = 0;
  let lengthM = 0;
  let vertices = 0;
  for (const ring of rings) {
    const mercator = ring.map(([x, y]) => toMercator(x, y));
    vertices += Math.max(0, ring.length - (kind === 'polygon' && ring.length > 1 ? 1 : 0));
    lengthM += ringLengthM(mercator);
    if (kind === 'polygon') areaM2 += Math.abs(shoelaceM2(mercator));
  }
  return {
    areaM2: kind === 'polygon' && areaM2 > 0 ? areaM2 : null,
    lengthM: (kind === 'polygon' || kind === 'line') && lengthM > 0 ? lengthM : null,
    vertices: vertices > 0 ? vertices : null,
  };
}

function ringsOf(geometry: unknown): Array<Array<readonly [number, number]>> {
  const type = geometryTypeOf(geometry);
  const coords = coordinatesOf(geometry);
  if (!type || coords == null) return [];
  if (type === 'Point') {
    const pos = asPosition(coords);
    return pos ? [[pos]] : [];
  }
  if (type === 'MultiPoint' || type === 'LineString') {
    return [asLine(coords)];
  }
  if (type === 'MultiLineString' || type === 'Polygon') {
    return asArray(coords).map(asLine).filter((ring) => ring.length > 0);
  }
  if (type === 'MultiPolygon') {
    const out: Array<Array<readonly [number, number]>> = [];
    for (const polygon of asArray(coords)) {
      const rings = asArray(polygon).map(asLine);
      if (rings[0]?.length) out.push(rings[0]);
    }
    return out;
  }
  return [];
}

function coordinatesOf(geometry: unknown): unknown {
  if (!geometry || typeof geometry !== 'object') return null;
  const record = geometry as { coordinates?: unknown; getCoordinates?: () => unknown };
  if (typeof record.getCoordinates === 'function') return record.getCoordinates();
  return record.coordinates;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function asPosition(value: unknown): readonly [number, number] | null {
  if (!Array.isArray(value) || value.length < 2) return null;
  const x = value[0];
  const y = value[1];
  return typeof x === 'number' && typeof y === 'number' && Number.isFinite(x) && Number.isFinite(y)
    ? [x, y]
    : null;
}

function asLine(value: unknown): Array<readonly [number, number]> {
  return asArray(value)
    .map(asPosition)
    .filter((pos): pos is readonly [number, number] => Boolean(pos));
}

function toMercator(x: number, y: number): readonly [number, number] {
  if (Math.abs(x) > 180 || Math.abs(y) > 90) return [x, y];
  const lon = (x * Math.PI) / 180;
  const lat = (Math.max(-85.05112878, Math.min(85.05112878, y)) * Math.PI) / 180;
  return [
    ORIGIN_SHIFT * (lon / Math.PI),
    ORIGIN_SHIFT * Math.log(Math.tan(Math.PI / 4 + lat / 2)) / Math.PI,
  ];
}

function shoelaceM2(ring: ReadonlyArray<readonly [number, number]>): number {
  if (ring.length < 3) return 0;
  let sum = 0;
  for (let i = 0; i < ring.length; i += 1) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[(i + 1) % ring.length];
    sum += x1 * y2 - x2 * y1;
  }
  return sum / 2;
}

function ringLengthM(ring: ReadonlyArray<readonly [number, number]>): number {
  let length = 0;
  for (let i = 1; i < ring.length; i += 1) {
    const dx = ring[i][0] - ring[i - 1][0];
    const dy = ring[i][1] - ring[i - 1][1];
    length += Math.hypot(dx, dy);
  }
  return length;
}

function formatCoord(value: number): string {
  if (!Number.isFinite(value)) return '—';
  if (Math.abs(value) > 180) return String(Math.round(value));
  return (Math.round(value * 1e6) / 1e6).toString();
}

function formatMeasure(value: number): string {
  if (!Number.isFinite(value)) return '—';
  if (value >= 1000) return Math.round(value).toLocaleString('en-US');
  return (Math.round(value * 10) / 10).toString();
}

function formatAttribute(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'NULL';
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
}
